import * as React from "react";
import { useEffectWithPolling } from "uxp/components";
import { SimpleTextProperty } from "../../../common/ui-props/SimpleTextProperty";
import { IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from "../../../common/common";
import { generateIconProp } from "../../../common/icon-selector/IconEditor";
import { Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartItemProperty, findAvailableFields, IChartItem, IChartResult } from "./ChartItemProperty";
import { BooleanGroupProperty } from "../../../common/ui-props/BooleanGroupProperty";
import { IContainerPropertyProps } from "../../../common/ui-props/ContainerProperty";
import { ITextProperty } from "../../../common/ui-props/TextProperty";
import { BooleanProperty } from "../../../common/ui-props/BooleanProperty";
import { createDefaultPropsForWidgetWrapperWithSidebar, WidgetWrapperWithSidebar } from "../../../common/widget-wrappers/WidgetWrapperWithSidebar";
import { ChartDataSortingProperty, IChartDataSortingProperty, SortConfig, extractChartDataSortConfig } from "../../../common/ui-props/ChartDataSortingProperty";
import { ChartAxisLabelProperty, IChartAxisLabelProperty, extractAxisLabelProps } from "../../../common/ui-props/ChartAxisLabelProperty";
import { SimpleNumberProperty } from "../../../common/ui-props/SimpleNumberProperty";
import { IMarginProperty, MarginProperty, extractMargins } from "../../../common/ui-props/MarginProperty";
import { IContextData, useInitialContextData } from "../../../../hooks/useContextData";
import WDFilterPanel from "../../../common/components/WDFilterPanel";
import { executeAndBind, getDependencyParameters, IDataSource, processInitialSource } from "../../../common/data-source-picker/datasource";
import { convertColor, hasValue } from "../../../../util";

interface IBarLineChartProps {
    headerContainer: IContainerPropertyProps
    title: ITextProperty;
    chartContainer: IContainerPropertyProps
    datasources: IDataSource[];
    xLabel: IChartAxisLabelProperty,
    yLabel: IChartAxisLabelProperty,
    margins: IMarginProperty,
    sortData: IChartDataSortingProperty,
    xField: string; // deperecated
    timeseries: boolean;
    refreshInterval: string;
    chartItems: IChartItem[];
    appearance: {
        showLegend: boolean;
        showGrid: boolean;
        showYAxis: boolean;
        stackBars: boolean;
    }
    filter?: any,
    legendTopMargin?: number
}

let randomDataMap: { [key: string]: number[] } = {};
function generateRandomData(field: string) {
    if (!randomDataMap[field]) {
        let randomData: number[] = [];
        for (let i = 0; i < 10; i++) {
            randomData.push(25 + Math.random() * 75);
        }
        randomDataMap[field] = randomData;
    }
    return randomDataMap[field];
}
function intFmt(x: string) {
    return Intl.NumberFormat().format(Number(x));
}

function sortChartData(chartData: any[], sorting: SortConfig, xField: string) {
    if (!Array.isArray(chartData)) return []

    chartData = [...chartData]

    if (sorting.sortBy === 'none') {
        return chartData;
    }

    if (sorting.sortBy == 'label') {
        if (!xField) return chartData
        return chartData.sort((a, b) => {
            if (typeof a[xField] == 'string') {
                return sorting.sortOrder == 'asc' ? a[xField].localeCompare(b[xField]) : b[xField].localeCompare(a[xField])
            } else if (typeof a[xField] == 'number') {
                return sorting.sortOrder == 'asc' ? a[xField] - b[xField] : b[xField] - a[xField]
            } else {
                return sorting.sortOrder == 'asc' ?
                    a[xField] > b[xField] ? 1 : (a[xField] === b[xField] ? 0 : -1)
                    : b[xField] > a[xField] ? 1 : (b[xField] === a[xField] ? 0 : -1)
            }
        })
    }
    else {
        const valueKey = Object.keys(chartData[0] || {}).find(s => s?.startsWith('0/'))
        if (!valueKey) return chartData
        return chartData.sort((a, b) => (sorting.sortOrder == 'asc' ? a[valueKey] - b[valueKey] : b[valueKey] - a[valueKey]))
    }
}

const ChartSortingOptions: { name: string, id: any }[] = [
    { name: 'None', id: 'none' },
    { name: 'By x axis', id: 'by-x-axis' },
]

export const BarLineChartWidget: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps<IBarLineChartProps>> = React.forwardRef((props, ref) => {

    const { uxpContext, designer, uiProps, initialSource } = props
    const XPRProps = createDefaultPropsForWidgetWrapperWithSidebar({
        'refreshInterval': SimpleTextProperty('Refresh Time', '60000'),
        'timeseries': BooleanProperty('Timeseries Data', false),
        'xLabel': ChartAxisLabelProperty({ axis: 'x', field: '', showAllLabels: false, rotation: 0, rotateDirection: 'clockwise', label: { value: '', offset: 0 } }, () => xValues),
        'yLabel': ChartAxisLabelProperty({ axis: 'y', rotation: 0, rotateDirection: 'clockwise', label: { value: '', offset: 0 }, showDecimals: false }, () => xValues),
        'margins': MarginProperty({ top: 50, bottom: 50, left: 50, right: 50 }),
        'sortData': ChartDataSortingProperty({ sortBy: 'none', sortOrder: 'asc', isTimeSeriesData: uiProps.timeseries }),
        'chartItems': ChartItemProperty('Chart Elements', [], uiProps.datasources, uxpContext, () => contextData),
        'appearance': BooleanGroupProperty('Appearance',
            { showLegend: false, showGrid: false, showYAxis: false, stackBars: false, },
            { showLegend: 'Show Legend', showGrid: 'Show Grid', showYAxis: 'Show Y-Axis', stackBars: 'Stack Bars' }),
        'legendTopMargin': SimpleNumberProperty('Legend Top Margin', 20)

    });

    let [chartSeries, setChartSeries] = React.useState<IChartResult[]>([]);
    const [loading, setLoading] = React.useState(false)
    let [contextData, setContextData] = React.useState<IContextData>({})
    const [IS, setIntitialSource] = React.useState(initialSource);

    const containerRef = React.useRef(null)
    const { isLoading, initialContextData } = useInitialContextData(props)


    function hasAtLeastOneChartElement(showError?: boolean): boolean {
        if (!uiProps?.chartItems?.length) {
            if (showError) {
                designer?.addErrors?.([{
                    id: 'no-chart-elements',
                    type: 'property',
                    error: 'Chart must contain at least one chart element',
                    containerId: 'bar-chart-container',
                    fieldId: 'chartItems'
                }])
            }

            return false
        }

        designer?.removeErrors?.(['no-chart-elements'])

        return true
    }

    function doesEveryChartElementContainADataSource(showError?: boolean): boolean {
        if (!uiProps?.chartItems?.every((element) => !!element.binding)) {
            if (showError) {
                designer?.addErrors?.([{
                    id: 'no-data-source',
                    type: 'property',
                    error: 'Every chart element must contain a data source',
                    containerId: 'bar-chart-container',
                    fieldClassName: 'no-data-source',
                }])
            }

            return false
        }
        designer?.removeErrors?.(['no-data-source'])
        return true
    }

    function validateBeforeSave() {
        if (!hasAtLeastOneChartElement(true)) return false
        if (!doesEveryChartElementContainADataSource(true)) return false
        return true
    }

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])


    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    React.useEffect(() => {
        processInitialSource(designer, uxpContext, uiProps, IS, 'dictionary-array')
            .then(res => {
                if (res) {
                    let { source } = res
                    let items = res.chartItems || []

                    items.push({
                        binding: source,
                        color: '#06f',
                        field: '',
                        label: '',
                        type: 'bar',
                        xTitle: '',
                        yTitle: '',
                    })

                    let newUIProps = Object.assign({}, { ...res }, { chartItems: items })
                    props.designer?.setUIProps(newUIProps)
                }
            })
            .catch(e => {
                console.log('exception ', e);

            })
    }, [IS])

    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    let xValues = React.useMemo(() => {
        let possibleValues: any = {};
        for (let item of chartSeries) {
            if (!item.type) {
                continue;
            }
            findAvailableFields(item.type, uiProps.datasources, possibleValues);
        }
        let pv = Object.keys(possibleValues);
        pv.sort();
        return pv;
    }, [chartSeries]);

    let interval = Number(uiProps?.refreshInterval) || 60000;

    // const { width, height, entry, unobserve, observe } = useDimensions({
    //     onResize: (p: any) => { },
    // });
    function renderLineSeries(result: IChartResult, index: number) {
        return <Line type={'monotone'} strokeWidth={2} yAxisId={result.type.field} dataKey={index + '/' + result.type.field} stroke={convertColor(result.type.color,contextData)} fill={convertColor(result.type.color,contextData)} name={result.type.label || ('Bar ' + index)} />
    }
    function renderAreaSeries(result: IChartResult, index: number) {
        return <Area fillOpacity={0.5} type={'monotone'} yAxisId={result.type.field} dataKey={index + '/' + result.type.field} fill={convertColor(result.type.color,contextData)} name={result.type.label || `Area ${index}`} />
    }
    function renderBarSeries(result: IChartResult, index: number) {
        return <Bar
            stackId={uiProps.appearance?.stackBars ? 'a' : null}
            radius={[2, 2, 0, 0]}
            maxBarSize={15}
            yAxisId={result.type.field}
            dataKey={index + '/' + result.type.field}
            fill={convertColor(result.type.color,contextData)}
            name={result.type.label || ('Line ' + index)}
        />
    }
    function renderChartSeries(result: IChartResult, index: number) {
        switch (result.type.type) {
            default:
            case 'line':
                return renderLineSeries(result, index);
            case "area":
                return renderAreaSeries(result, index);
            case "bar":
                return renderBarSeries(result, index);
        }
    }
    function formatXAxis(item: any) {
        if (!uiProps.timeseries) {
            return item;
        }
        return new Date(item).toLocaleString();
    }

    let xf = uiProps?.xLabel?.field || '__x';

    let deps: any[] = [];
    if (designer?.mode == 'preview' || designer?.mode == 'thumbnail') {
        deps = [uiProps.chartItems];
    }
    let sourceDeps = getDependencyParameters(uiProps.chartItems?.map(ci => ci.binding), uiProps.datasources, contextData);
    deps = [...deps, ...sourceDeps, contextData, uiProps];
    console.log('Data Source Dependencies', deps);
    useEffectWithPolling(uxpContext, "chart/1", Number(interval) || 60000, async () => {
        if (designer?.mode == 'preview') {
            let results: IChartResult[] = [];
            for (let item of uiProps.chartItems || []) {
                let data = [];

                for (var i = 0; i < 10; i++) {
                    data.push({
                        [xf]: uiProps.timeseries ? (new Date(Number(new Date()) - (24 - i) * 3600 * 1000)) : (`Label ${i}`),
                        [item.field]: generateRandomData(item.field)[i],
                    });
                }
                results.push({ data, type: item });
            }
            setChartSeries(results);
            return;
        }
        if (designer?.mode != 'thumbnail') {
            let results: IChartResult[] = [];
            for (let item of uiProps.chartItems) {
                let params: any = { filters: contextData?.filters || {} }

                try {
                    let data = await executeAndBind(uxpContext, item.binding, uiProps.datasources, params, contextData);
                    results.push({ data, type: item });
                }
                catch (e) {
                    console.log('error getting data', e)
                }
            }
            setChartSeries(results);
        }
    }, deps);
    let chartData: any[] = [];
    let uniqueFields: any = {};
    if (chartSeries.length == 0 && (designer?.mode == 'thumbnail' || designer?.mode == 'preview')) {
        xf = 'x';
        chartSeries = [
            {
                data: generateRandomData('nodata').map((y, x) => ({ x, y })),
                type: {
                    binding: { path: '', source: '' },
                    color: 'rgba(127,127,127,0.5)',
                    field: 'y',
                    label: 'Data',
                    type: 'bar',
                    xTitle: '',
                    yTitle: '',
                    units: ''
                }
            }
        ]
    }
    for (let sri in chartSeries) {
        let sr = chartSeries[sri];
        uniqueFields[sr.type.field] = 1;
        let data = sr.data;
        for (let row of data) {
            let found = chartData.find(x => x[xf] == row[xf]);
            if (!found) {
                found = { [xf]: row[xf] };
                chartData.push(found);
            }
            found[sri + '/' + sr.type.field] = Number(row[sr.type.field]);
        }
    }


    let fields = Object.keys(uniqueFields);
    let showLegend = !!uiProps.appearance?.showLegend;

    const xLabelProps = extractAxisLabelProps(uiProps.xLabel)
    const yLabelProps = extractAxisLabelProps(uiProps?.yLabel)
    const margins = extractMargins(uiProps.margins)
    const sortConfig = extractChartDataSortConfig(uiProps.sortData)

    console.log('chartdata', xLabelProps, yLabelProps)

    const sortedChartData = React.useMemo(() => {
        const sorted = sortChartData(chartData, sortConfig, xf)

        return sorted
    }, [chartData, sortConfig])

    const getUnitsForLabel = (label: string) => {
        const record = uiProps.chartItems?.find(c => c.label === label)
        return record ? record?.units : null;
    }
    const CustomTooltip = ({ payload, label }: any) => {
        let labelOnTooltip = label;
        if (uiProps.timeseries) {
            labelOnTooltip = new Date(label).toLocaleString();
        }
        return (
            <div style={{ backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', padding: '0 10px'}}>
                <p style={{padding: '0'}}>{labelOnTooltip}</p>
                {payload?.map((p: any) => <p style={{color: p?.stroke, padding: '0'}}>{p?.name}: {intFmt(Number(p?.value).toFixed(2))} {getUnitsForLabel(p.name)}</p>)}
            </div>
        );
    };
    
    return <WidgetWrapperWithSidebar
        isInnerField={props.isInnerField}
        className='uxp-custom-donut-chart-template'
        contextData={contextData}
        propDef={XPRProps}
        uiProps={uiProps}
        elementId='bar-chart-container'
        showFilterWithoutFields={(uiProps?.filter?.enableExport == false) ? false : true}
        designer={props.designer}
        defaultTitle='Bar Chart'
        containerRef={containerRef}
        filterSection={() =>
            <WDFilterPanel
                designer={props.designer}
                uxpContext={props.uxpContext}
                uiProps={uiProps}
                containerRef={containerRef}
                data={contextData || {}}
                onChangeFilters={(data) => {
                    setContextData(prev => ({ ...prev, filters: data?.filters || {} }))
                }}
                onClearFilters={() => {
                    setContextData(prev => ({ ...prev, filters: {} }))
                }}
                onOpenPanel={() => { }}
            />}
    >
        <ResponsiveContainer>

            <ComposedChart
                barCategoryGap={2}
                barSize={15}
                data={sortedChartData}
                margin={margins}
            >

                <Tooltip 
                    // formatter={(value, name, entry, index) => {
                    //     console.log("On tool tip: ", entry)
                    //     return `${intFmt(Number(value).toFixed(2))}`
                    // }} 
                    content={<CustomTooltip />}
                />

                {showLegend &&
                    <Legend align="center" verticalAlign="bottom"
                        wrapperStyle={{ paddingTop: hasValue(uiProps.legendTopMargin, true) ? uiProps.legendTopMargin : 20 }}
                    />
                }

                {uiProps.appearance?.showGrid &&
                    <CartesianGrid strokeWidth={1} horizontal={true} vertical={false} strokeOpacity={0.5} />
                }

                <XAxis
                    dataKey={xf}
                    tickFormatter={formatXAxis}
                    {...xLabelProps}
                />
                {
                    fields.map((f, i) => <YAxis
                        orientation={(i == 0) ? 'left' : 'right'}
                        hide={i > 1 || !uiProps.appearance?.showYAxis}
                        yAxisId={f}
                        {...yLabelProps}
                        textAnchor='end'
                    />)
                }

                {
                    chartSeries.map((r, i) => renderChartSeries(r, i))
                }

            </ComposedChart>
        </ResponsiveContainer>
    </WidgetWrapperWithSidebar>;

});

registerCardTemplate({
    id: 'bar-line-chart',
    name: 'Bar/Line Chart',
    description: 'A chart with multiple lines or bars. Multiple data sources and 2 Y-Axis\'s can be configured. Visually data is represented as either a line, area chart, or stacked bars. The widget can be configured to refresh automatically at regular intervals.',
    template: BarLineChartWidget,
    icon: generateIconProp('chartBar'),
    canDragDropFields: true,
    complexity: 'advanced',
    preLoader: 'bar-chart',
    expectedSchema: 'dictionary-array'
});

